import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { testimonials, successMetrics, trustBadges } from '../data/testimonials';

const Home = () => {
  // Memoize data for performance
  const memoizedTestimonials = useMemo(() => testimonials, []);
  const memoizedMetrics = useMemo(() => successMetrics, []);
  const memoizedTrustBadges = useMemo(() => trustBadges, []);

  // Memoize schema objects for better performance
  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Ravana Migration Pvt Ltd',
    description: "Sri Lanka's No.1 Study Abroad Consultant for Germany, Canada, Australia, UK, and New Zealand",
    url: 'https://ravanamigration.com',
    logo: 'https://ravanamigration.com/img/logo.png',
    sameAs: [
      'https://www.facebook.com/ravanamigration',
      'https://www.linkedin.com/company/ravana-migration'
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'Sri Lanka'
    },
    areaServed: ['Germany', 'Canada', 'Australia', 'United Kingdom', 'New Zealand']
  }), []);

  const websiteSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Ravana Migration',
    url: 'https://ravanamigration.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://ravanamigration.com/blog/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  }), []);

  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do you support more than Germany and Canada?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — through Flight Plan™ Alliance we support Germany, Canada, Australia, the UK, and New Zealand.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I get a study loan for Canada?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. We guide loans covering up to 75% of tuition, typically repayable over 4–7 years at ~11% APR (subject to lender eligibility/terms).'
        }
      },
      {
        '@type': 'Question',
        name: 'Is the fee refundable?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'DIY is non-refundable. Plus and PRO include partial refunds per your signed service agreement.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is the Application Success Guarantee?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'With PRO, if all applications are rejected, we provide a free re-application next semester (worth LKR 850,000).'
        }
      },
      {
        '@type': 'Question',
        name: 'How many university applications are included?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'PRO includes minimum 7 (5 public + 2 private backups). DIY and Plus vary by case; your consultant will advise.'
        }
      }
    ]
  }), []);

  // Scroll progress indicator effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('scroll-progress');
      if (scrollProgress) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }

      // Intersection Observer for animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
          }
        });
      }, observerOptions);

      // Observe elements
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Page scroll progress">
        <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300 ease-out" style={{ width: '0%' }} id="scroll-progress" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}></div>
      </div>
      <Helmet>
        <title>Study in Germany, Canada, Australia, UK & New Zealand | Ravana Migration — 3 Transparent Packages</title>
        <meta name="description" content="Apply to 5 countries with one partner. DIY, Plus, and PRO packages. Plus now includes DE health insurance setup, blocked account guidance, and Canada loan assistance (up to 75% tuition, 4–7 yrs at ~11% APR). PRO adds multi-country coverage, 25% family visa discount, 100GB cloud + dedicated email, and Google Gemini Pro for 1 year with 2TB storage." />
        <meta name="keywords" content="study in Germany, study in Canada, study in Australia, study in UK, study in New Zealand, German student visa Sri Lanka, Canadian study visa, tuition-free Germany, study loans Canada, visa consultant Sri Lanka, Ravana Migration, study abroad, Ravana Flight Plan" />

        {/* Open Graph */}
        <meta property="og:title" content="Study in Germany, Canada, Australia, UK & New Zealand | Ravana Migration" />
        <meta property="og:description" content="One partner for 5 countries. DIY, Plus & PRO packages. 99% success rate. Contract-first approach with transparent pricing. Canada study loans up to 75% tuition." />
        <meta property="og:image" content="https://www.ravanamigration.com/img/meta-image.png" />
        <meta property="og:url" content="https://www.ravanamigration.com" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Study in Germany, Canada, Australia, UK & New Zealand | Ravana Migration" />
        <meta name="twitter:description" content="One partner for 5 countries. DIY, Plus & PRO packages with transparent pricing. Canada study loans up to 75% tuition. 99% success rate." />
        <meta name="twitter:image" content="https://www.ravanamigration.com/img/meta-image.png" />

        {/* Additional SEO tags */}
        <link rel="canonical" href="https://www.ravanamigration.com" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />

        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero Section with enhanced visual design */}
      <div className="relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-float" style={{ animationDelay: '4s' }}></div>
        </div>

        <div className="section-muted py-16 sm:py-20 lg:py-24 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Trust Badges */}
            <div
              className="flex flex-wrap justify-center gap-3 mb-10 animate-fade-in-up"
              role="list"
              aria-label="Trust indicators"
              style={{ opacity: 0 }}
            >
              {memoizedTrustBadges.map((badge, index) => (
                <div
                  key={index}
                  className={`chip hover-lift cursor-pointer ${badge.highlight ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''} transform transition-all duration-300 hover:scale-105`}
                  role="listitem"
                  aria-label={badge.text}
                >
                  <span aria-hidden="true" className="text-lg">{badge.icon}</span>
                  <span className={`${badge.highlight ? 'font-semibold' : ''} text-sm`}>{badge.text}</span>
                  {badge.highlight && <span className="sr-only">Featured</span>}
                </div>
              ))}
            </div>

            {/* Main Hero Content */}
            <div className="text-center">
              <div className="animate-fade-in-scale mb-8" style={{ opacity: 0 }}>
                <img
                  src="../img/logo.png"
                  alt="Ravana Migration Logo"
                  className="w-full max-w-2xl mx-auto h-32 sm:h-40 object-contain hover-scale transition-transform duration-500"
                />
              </div>

              <h1 className="heading-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4 leading-tight animate-slide-in-right" style={{ opacity: 0 }}>
                Sri Lanka's premier AI-powered{' '}
                <span className="relative inline-block">
                  <span className="relative z-10">study abroad partner</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-yellow-400 opacity-30" viewBox="0 0 200 20" fill="currentColor">
                    <path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" />
                  </svg>
                </span>
              </h1>

              <div className="rule mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: '0.3s', opacity: 0 }}></div>

              <div className="animate-fade-in-up" style={{ animationDelay: '0.4s', opacity: 0 }}>
                <p className="lede text-xl sm:text-2xl md:text-3xl max-w-4xl mx-auto mb-4 leading-relaxed font-medium">
                  🇩🇪 🇨🇦 🇦🇺 🇬🇧 🇳🇿
                </p>
                <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-10 text-gray-700 leading-relaxed">
                  From tuition-free universities in Germany to funded programs in Canada -
                  we make your international education dreams a reality with expert guidance every step of the way with AI and standardized processes.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: '0.5s', opacity: 0 }}>
                <Link
                  to="/choice"
                  className="btn btn-accent text-lg px-8 py-4 hover-lift group relative overflow-hidden"
                >
                  <span className="relative z-10">Start Your Free Assessment</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/choice"
                  className="btn btn-outline text-lg px-8 py-4 hover-lift group"
                >
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Talk to an Expert</span>
                </Link>
              </div>

              {/* Quick stats */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-8 text-sm text-gray-600 animate-fade-in-up" style={{ animationDelay: '0.6s', opacity: 0 }}>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">99% Success Rate</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span className="font-semibold">500+ Students</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="font-semibold">5-Star Rated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Flight Plan™ Alliance Partner Section */}
        <div className="mb-20 mt-16">
          <div className="text-center mb-12 animate-on-scroll" style={{ opacity: 0 }}>
            <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Flight Plan™ Alliance Partner
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto font-medium">
              One Profile. Five Destinations.
            </p>
            <div className="rule mx-auto mt-4"></div>
            <p className="text-base sm:text-lg max-w-3xl mx-auto mt-6 text-gray-600">
              Flight Plan™ is an alliance that provides standardised migration services in Sri Lanka that is AI powered.
            </p>
            <p className="text-base sm:text-lg max-w-3xl mx-auto mt-4 text-gray-700 font-semibold">
              Ravana Migration is a proud alliance partner.
            </p>
            <p className="text-base sm:text-lg max-w-3xl mx-auto mt-4 text-gray-600">
              Build a single profile and apply across Germany, Canada, Australia, the UK, and New Zealand with tailored guidance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 animate-on-scroll" style={{ opacity: 0 }}>
            {/* Germany */}
            <div className="country-card group cursor-pointer animate-on-scroll" style={{ animationDelay: '0.1s', opacity: 0 }}>
              <div className="flex flex-col items-center h-full">
                <div className="text-6xl mb-4 transform transition-transform group-hover:scale-110">🇩🇪</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Germany</h3>
                <div className="space-y-2 text-sm mb-6 flex-grow">
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bachelor's · Master's · PhD
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Chancenkarte (Job Seeker)
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Au Pair · Language Visa
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Ausbildung
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 text-sm w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <p className="font-semibold text-blue-900">Highlight</p>
                  </div>
                  <p className="text-blue-800">Tuition-free public universities + blocked account guidance</p>
                </div>
              </div>
            </div>

            {/* Canada */}
            <div className="country-card group cursor-pointer animate-on-scroll" style={{ animationDelay: '0.2s', opacity: 0 }}>
              <div className="flex flex-col items-center h-full">
                <div className="text-6xl mb-4 transform transition-transform group-hover:scale-110">🇨🇦</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Canada</h3>
                <div className="space-y-2 text-sm mb-6 flex-grow">
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Certificate · Diploma
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bachelor's · PG Diploma
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Master's · PhD
                  </p>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 text-sm w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <p className="font-semibold text-yellow-900">Highlight</p>
                  </div>
                  <p className="text-yellow-800">Loan up to 75% tuition, repayable over 4–7 years at 11% APR</p>
                </div>
              </div>
            </div>

            {/* Australia */}
            <div className="country-card group cursor-pointer animate-on-scroll" style={{ animationDelay: '0.3s', opacity: 0 }}>
              <div className="flex flex-col items-center h-full">
                <div className="text-6xl mb-4 transform transition-transform group-hover:scale-110">🇦🇺</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Australia</h3>
                <div className="space-y-2 text-sm mb-6 flex-grow">
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bachelor's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Master's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Top-up Degree
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4 text-sm w-full">
                  <p className="text-purple-800 font-medium">World-class education with pathway options</p>
                </div>
              </div>
            </div>

            {/* United Kingdom */}
            <div className="country-card group cursor-pointer animate-on-scroll" style={{ animationDelay: '0.4s', opacity: 0 }}>
              <div className="flex flex-col items-center h-full">
                <div className="text-6xl mb-4 transform transition-transform group-hover:scale-110">🇬🇧</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">United Kingdom</h3>
                <div className="space-y-2 text-sm mb-6 flex-grow">
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bachelor's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Master's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Top-up Degree
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 text-sm w-full">
                  <p className="text-red-800 font-medium">Historic institutions with global recognition</p>
                </div>
              </div>
            </div>

            {/* New Zealand */}
            <div className="country-card group cursor-pointer animate-on-scroll" style={{ animationDelay: '0.5s', opacity: 0 }}>
              <div className="flex flex-col items-center h-full">
                <div className="text-6xl mb-4 transform transition-transform group-hover:scale-110">🇳🇿</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">New Zealand</h3>
                <div className="space-y-2 text-sm mb-6 flex-grow">
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bachelor's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Master's
                  </p>
                  <p className="text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Work Visa
                  </p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-xl p-4 text-sm w-full">
                  <p className="text-teal-800 font-medium">High quality of life with post-study work rights</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-10 animate-on-scroll" style={{ opacity: 0 }}>
            <Link to="/choice" className="btn btn-accent text-lg px-8 py-4 hover-lift group inline-flex items-center gap-2">
              <span>See If You're Eligible</span>
              <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Pricing Packages Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-on-scroll" style={{ opacity: 0 }}>
            <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Packages
            </h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              All packages include eligibility assessment, profile setup, document checklists, and client portal access.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 animate-on-scroll" style={{ opacity: 0 }}>
            {/* DIY Package */}
            <div className="package-card hover-lift animate-on-scroll" style={{ animationDelay: '0.1s', opacity: 0 }}>
              <div className="text-center mb-6 pt-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Flight Plan™ DIY</h3>
                <p className="text-sm text-gray-600 mb-4 font-medium">Self Navigator</p>
                <div className="mb-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-bold text-gray-900">LKR 400,000</span>
                  </div>
                  <span className="text-sm text-gray-600 mt-2 inline-block">· Non-refundable</span>
                </div>
                <p className="text-sm text-gray-700 mb-6">For confident self-starters who want expert verification.</p>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Document verification for up to 5 countries</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">SOP/CV/LoR format standards + 1 feedback round</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Portal access & status tracking</p>
                </div>
              </div>

              <Link to="/choice" className="btn btn-outline w-full hover-lift group flex items-center justify-center gap-2">
                <span>Choose DIY</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>

            {/* Plus Package */}
            <div className="package-card hover-lift relative animate-on-scroll pt-8" style={{ animationDelay: '0.2s', opacity: 0 }}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-xl whitespace-nowrap">
                  MOST POPULAR
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Flight Plan™ Plus</h3>
                <p className="text-sm text-gray-600 mb-4 font-medium">Guided Success</p>
                <div className="mb-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-bold text-gray-900">LKR 850,000</span>
                  </div>
                  <span className="text-sm text-gray-600 mt-2 inline-block">· Partially refundable</span>
                </div>
                <p className="text-sm text-gray-700 mb-6">End-to-end handling for one country with premium essentials.</p>
              </div>

              <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Everything in DIY, plus:
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Full university + visa management for 1 country</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">SOP/LoR editing & application management</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Visa documentation & appointment support</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Public health insurance setup (DE)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Blocked account setup & guidance (DE)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Study Loan Assistance (Canada): Up to 75% tuition, 4–7 yrs at 11% APR</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Referral commission LKR 25,000 per successful referral</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 mt-1">•</span>
                  <p className="text-sm text-gray-500">Structured payment (60% / 40%)</p>
                </div>
              </div>

              <Link to="/choice" className="btn btn-accent w-full hover-lift group flex items-center justify-center gap-2">
                <span>Choose Plus</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>

            {/* PRO Package */}
            <div className="package-card pro hover-lift relative animate-on-scroll pt-8" style={{ animationDelay: '0.3s', opacity: 0 }}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-sm font-bold px-6 py-2 rounded-full shadow-xl flex items-center gap-1 whitespace-nowrap">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  BEST VALUE
                </span>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Flight Plan™ Pro</h3>
                <p className="text-sm text-gray-600 mb-4 font-medium">All-Inclusive Elite</p>
                <div className="mb-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl sm:text-5xl font-bold text-gray-900">LKR 1,190,000</span>
                  </div>
                  <span className="text-sm text-gray-600 mt-2 inline-block">· Partially refundable</span>
                </div>
                <p className="text-sm text-gray-700 mb-6">Maximum safety, global reach, and VIP support across all 5 countries.</p>
              </div>

              <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Everything in Plus, plus:
              </p>
              <div className="space-y-2 mb-8">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Multi-country coverage (all 5 included)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Minimum 7 university applications (5 public + 2 private backup)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Application Success Guarantee: Free re-application next semester if all rejected (worth LKR 850,000)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Dedicated consultant with priority support & direct line</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Free IELTS preparation course (worth LKR 80,000)</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Travel insurance (first month) included</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Student & alumni network access</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Referral commission LKR 50,000 per successful referral</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">25% Family Visa Discount — spouse/dependents</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">100GB cloud storage with dedicated email</p>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">Google Gemini Pro for 1 year with 2TB storage</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 mt-1">•</span>
                  <p className="text-sm text-gray-500">Structured payment (40% / 30% / 30%)</p>
                </div>
              </div>

              <Link to="/choice" className="btn btn-accent w-full hover-lift group flex items-center justify-center gap-2">
                <span>Go PRO</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>



        {/* Why Choose Ravana Migration */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="heading-tight text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Why Choose Ravana Migration?</h2>
            <p className="text-lg text-gray-600">Four reasons students trust us</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-6 text-center card-hover">
              <div className="text-4xl mb-3">🤝</div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Contract-First, Always</h3>
              <p className="text-sm text-gray-600">No surprise fees — every service in writing.</p>
            </div>
            <div className="card p-6 text-center card-hover">
              <div className="text-4xl mb-3">💳</div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Bank Transfers & Invoices Only</h3>
              <p className="text-sm text-gray-600">100% transparent payments.</p>
            </div>
            <div className="card p-6 text-center card-hover">
              <div className="text-4xl mb-3">📈</div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">99% Success Rate</h3>
              <p className="text-sm text-gray-600">For qualified applicants across our core pathways.</p>
            </div>
            <div className="card p-6 text-center card-hover">
              <div className="text-4xl mb-3">📱</div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Best-in-Class Portal</h3>
              <p className="text-sm text-gray-600">Real-time status, tasks, and file management.</p>
            </div>
          </div>

          {/* More peace of mind section */}
          <div className="mt-8 bg-gray-50 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">More peace of mind:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚖️</span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Expert immigration professionals</p>
                  <p className="text-xs text-gray-600">handling your file</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🧾</span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">No hidden costs</p>
                  <p className="text-xs text-gray-600">pay only what's on the agreement</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🧭</span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Clear timelines</p>
                  <p className="text-xs text-gray-600">deliverables, and communication</p>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* PRO Spotlight */}
        <div className="mb-20 animate-on-scroll" style={{ opacity: 0 }}>
          <div className="card p-8 lg:p-12 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-200 hover-lift">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-sm font-bold px-6 py-2 rounded-full mb-4 shadow-lg">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                PRO SPOTLIGHT
              </span>
              <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Flight Plan™ Pro = Maximum Safety + Global Reach
              </h2>
              <div className="rule mx-auto"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">📝</div>
                <p className="text-sm font-bold text-gray-900">7+ applications</p>
                <p className="text-xs text-gray-600 mt-1">5 public + 2 private</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🛡️</div>
                <p className="text-sm font-bold text-gray-900">Retry guarantee</p>
                <p className="text-xs text-gray-600 mt-1">Worth LKR 850K</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">👥</div>
                <p className="text-sm font-bold text-gray-900">Dedicated consultant</p>
                <p className="text-xs text-gray-600 mt-1">Priority support</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">📚</div>
                <p className="text-sm font-bold text-gray-900">Free IELTS</p>
                <p className="text-xs text-gray-600 mt-1">Worth LKR 80K</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">✈️</div>
                <p className="text-sm font-bold text-gray-900">Travel insurance</p>
                <p className="text-xs text-gray-600 mt-1">First month</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🏥</div>
                <p className="text-sm font-bold text-gray-900">DE insurance & blocked account</p>
                <p className="text-xs text-gray-600 mt-1">Full support</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🤝</div>
                <p className="text-sm font-bold text-gray-900">Alumni network</p>
                <p className="text-xs text-gray-600 mt-1">Student community</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💰</div>
                <p className="text-sm font-bold text-gray-900">LKR 50,000 referral</p>
                <p className="text-xs text-gray-600 mt-1">Per success</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">👨‍👩‍👧</div>
                <p className="text-sm font-bold text-gray-900">Family Visa –25%</p>
                <p className="text-xs text-gray-600 mt-1">Spouse/dependents</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">☁️</div>
                <p className="text-sm font-bold text-gray-900">100GB cloud</p>
                <p className="text-xs text-gray-600 mt-1">+ dedicated email</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🤖</div>
                <p className="text-sm font-bold text-gray-900">Google Gemini Pro</p>
                <p className="text-xs text-gray-600 mt-1">1 year + 2TB storage</p>
              </div>
              <div className="bg-white rounded-xl p-5 text-center hover-lift shadow-sm hover:shadow-md transition-all duration-300 group">
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">🌍</div>
                <p className="text-sm font-bold text-gray-900">Multi-country</p>
                <p className="text-xs text-gray-600 mt-1">All 5 included</p>
              </div>
            </div>

            <div className="text-center">
              <Link to="/choice" className="btn btn-accent text-lg px-8 py-4 hover-lift group inline-flex items-center gap-2">
                <span>Upgrade to PRO</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>



        {/* How It Works Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-on-scroll" style={{ opacity: 0 }}>
            <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">How It Works</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">A guided journey from assessment to visa</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-on-scroll" style={{ opacity: 0 }}>
            <div className="card p-6 text-center hover-lift group animate-on-scroll" style={{ animationDelay: '0.1s', opacity: 0 }}>
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-blue-600 mb-2">Step 1</div>
              <h3 className="font-bold text-gray-900 mb-3 text-lg">Free Assessment</h3>
              <p className="text-sm text-gray-600">6 minutes online - Get instant eligibility results</p>
            </div>
            <div className="card p-6 text-center hover-lift group animate-on-scroll" style={{ animationDelay: '0.2s', opacity: 0 }}>
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-purple-600 mb-2">Step 2</div>
              <h3 className="font-bold text-gray-900 mb-3 text-lg">Consultation</h3>
              <p className="text-sm text-gray-600">Eligibility review & personalized pathway plan</p>
            </div>
            <div className="card p-6 text-center hover-lift group animate-on-scroll" style={{ animationDelay: '0.3s', opacity: 0 }}>
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-green-600 mb-2">Step 3</div>
              <h3 className="font-bold text-gray-900 mb-3 text-lg">Applications</h3>
              <p className="text-sm text-gray-600">University + visa file handled by experts</p>
            </div>
            <div className="card p-6 text-center hover-lift group animate-on-scroll" style={{ animationDelay: '0.4s', opacity: 0 }}>
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <div className="text-2xl font-bold text-yellow-600 mb-2">Step 4</div>
              <h3 className="font-bold text-gray-900 mb-3 text-lg">Visa & Departure</h3>
              <p className="text-sm text-gray-600">Pre-departure, insurance, onboarding support</p>
            </div>
          </div>
          <div className="text-center mt-10 animate-on-scroll" style={{ opacity: 0 }}>
            <Link to="/choice" className="btn btn-accent text-lg px-8 py-4 hover-lift group inline-flex items-center gap-2">
              <span>Begin Your Assessment</span>
              <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-on-scroll" style={{ opacity: 0 }}>
            <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Got questions? We've got answers.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-on-scroll" style={{ opacity: 0 }}>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">Do you support more than Germany and Canada?</h3>
                  <p className="text-sm text-gray-700">Yes — through Flight Plan™ Alliance we support Germany, Canada, Australia, the UK, and New Zealand.</p>
                </div>
              </div>
            </div>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">Can I get a study loan for Canada?</h3>
                  <p className="text-sm text-gray-700">Yes. We guide loans covering up to 75% of tuition, typically repayable over 4–7 years at ~11% APR (subject to lender eligibility/terms).</p>
                </div>
              </div>
            </div>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">Is the fee refundable?</h3>
                  <p className="text-sm text-gray-700">DIY is non-refundable. Plus and PRO include partial refunds per your signed service agreement.</p>
                </div>
              </div>
            </div>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">What is the Application Success Guarantee?</h3>
                  <p className="text-sm text-gray-700">With PRO, if all applications are rejected, we provide a free re-application next semester (worth LKR 850,000).</p>
                </div>
              </div>
            </div>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">How many university applications are included?</h3>
                  <p className="text-sm text-gray-700">PRO includes minimum 7 (5 public + 2 private backups). DIY and Plus vary by case; your consultant will advise.</p>
                </div>
              </div>
            </div>
            <div className="card p-6 card-hover group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 text-lg group-hover:text-blue-600 transition-colors">What support do you provide after arrival?</h3>
                  <p className="text-sm text-gray-700">We provide comprehensive pre-departure briefings, airport pickup coordination, accommodation assistance, and ongoing support through our alumni network.</p>
                </div>
              </div>
            </div>
          </div>
        </div>




        {/* Testimonials Section */}
        <div className="mb-20">
          <div className="text-center mb-12 animate-on-scroll" style={{ opacity: 0 }}>
            <h2 className="heading-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What Our Students Say</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600 mb-2">99% Success Rate • ⭐⭐⭐⭐⭐ Google Reviews</p>
            <p className="text-sm text-gray-500">Real reviews from real students</p>
          </div>

          {/* Success Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 animate-on-scroll" style={{ opacity: 0 }}>
            {memoizedMetrics.map((metric, index) => (
              <div key={index} className="card text-center p-6 hover-lift group" role="listitem">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-3 group-hover:scale-110 transition-transform">
                  {metric.value}{metric.suffix}
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-2">{metric.label}</div>
                <div className="text-xs text-gray-600">{metric.description}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-on-scroll" style={{ opacity: 0 }} role="list" aria-label="Student testimonials">
            {memoizedTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="card card-hover group relative overflow-hidden" role="listitem">
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-purple-50/0 group-hover:from-blue-50/50 group-hover:to-purple-50/50 transition-all duration-300"></div>

                <div className="relative">
                  {/* Star Rating */}
                  <div className="flex items-center gap-1 mb-4" aria-label={`${testimonial.rating} out of 5 stars`}>
                    {[...Array(testimonial.rating)].map(() => (
                      <svg key={Math.random()} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="sr-only">{testimonial.rating} out of 5 stars</span>
                  </div>

                  {/* Quote */}
                  <p className="text-gray-700 mb-6 text-sm leading-relaxed">"{testimonial.quote}"</p>

                  {/* Customer Info */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center">
                      <div
                        className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center font-medium text-white mr-3 shadow-md"
                        aria-hidden="true"
                      >
                        {testimonial.initials}
                      </div>
                      <div className="text-left">
                        <span className="font-semibold text-gray-900 text-sm block">{testimonial.name}</span>
                        <div className="text-xs text-gray-600 flex items-center gap-1">
                          <span>{testimonial.country}</span>
                          <span>•</span>
                          <span>{testimonial.program}</span>
                        </div>
                      </div>
                    </div>
                    {/* Verification Badge */}
                    {testimonial.verified && (
                      <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full" title="Verified Review">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" aria-label="Verified">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs font-medium text-green-700">Verified</span>
                        <span className="sr-only">Verified Review</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 animate-on-scroll" style={{ opacity: 0 }}>
            <a
              href="https://www.google.com/search?q=Ravana+Migration+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline inline-flex items-center gap-2 hover-lift group"
              aria-label="View all Google Reviews for Ravana Migration"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              <span>View Google Reviews</span>
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="mb-16 animate-on-scroll" style={{ opacity: 0 }}>
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700"></div>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float" style={{ animationDelay: '2s' }}></div>
            </div>

            <div className="relative text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Ready to Begin Your Global Journey?
              </h2>
              <div className="rule mx-auto mb-6 bg-white/30"></div>
              <p className="text-white mb-10 text-lg sm:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
                Join hundreds of students now studying in Germany, Canada, Australia, the UK, and New Zealand.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <Link
                  to="/choice"
                  className="btn bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group"
                >
                  <span>Start Your Free Assessment</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/choice"
                  className="btn btn-outline text-white border-white/80 hover:bg-white hover:text-blue-600 text-lg px-8 py-4 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group"
                >
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Speak to a Consultant</span>
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-white px-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">No sign-up required</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">6 minutes</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Instant results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mobile CTA - respect safe area, avoid overlap with chat bubble */}
      <div
        className="fixed left-4 right-24 z-50 md:hidden"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
      >
        <Link
          to="/choice"
          className="btn btn-accent w-full max-w-full text-center text-sm px-4 py-3 shadow-2xl rounded-xl animate-pulse-slow flex items-center justify-center overflow-hidden"
          aria-label="Start your free assessment to study abroad"
        >
          <svg className="w-5 h-5 inline-block mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="truncate">Start Free Assessment →</span>
        </Link>
      </div>
    </>
  );
};

export default Home;
